import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { useAuth } from '@/hooks/useAuth'
import AuthLayout from '@/components/auth/AuthLayout'
import SocialButtons from '@/components/auth/SocialButtons'

interface PlanOption {
  slug: string
  name: string
  price: number
  price_formatted: string
  is_featured?: boolean
}

const registerSchema = z.object({
  email: z.string().email('Invalid email address'),
  username: z.string().min(3, 'Username must be at least 3 characters'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
  acceptTerms: z.boolean().refine(val => val === true, {
    message: 'You must accept the terms and conditions',
  }),
  planId: z.string().min(1, 'Please select a subscription plan'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
})

type RegisterForm = z.infer<typeof registerSchema>

export default function Register() {
  const { t } = useTranslation('auth')
  const { register: registerUser, isLoading } = useAuth()
  const [searchParams] = useSearchParams()
  const [apiError, setApiError] = useState('')
  const [plans, setPlans] = useState<PlanOption[]>([])
  const [isLoadingPlans, setIsLoadingPlans] = useState(true)

  // Get plan_id from URL query param (e.g., from pricing page)
  const planIdFromUrl = searchParams.get('plan_id')

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      planId: planIdFromUrl || '',
    },
  })

  const selectedPlanId = watch('planId')

  // Fetch available plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch('/api/v1/subscriptions/plans')
        if (response.ok) {
          const result = await response.json()
          // Filter out free tier - it's not purchasable
          const purchasablePlans = (result.data || []).filter((p: PlanOption) => p.slug !== 'free')
          setPlans(purchasablePlans)
        }
      } catch (error) {
        console.error('Failed to fetch plans:', error)
      } finally {
        setIsLoadingPlans(false)
      }
    }

    fetchPlans()
  }, [])

  // Update form when planIdFromUrl changes
  useEffect(() => {
    if (planIdFromUrl) {
      setValue('planId', planIdFromUrl)
    }
  }, [planIdFromUrl, setValue])

  const onSubmit = async (data: RegisterForm) => {
    setApiError('')
    try {
      const response = await registerUser({
        email: data.email,
        password: data.password,
        password_confirmation: data.confirmPassword,
        username: data.username,
        first_name: data.firstName,
        last_name: data.lastName,
        accept_terms: data.acceptTerms,
        plan_id: data.planId,
      })

      // If checkout_url is returned, redirect to Stripe checkout
      if (response.checkout_url) {
        window.location.href = response.checkout_url
        return
      }

      // Redirect to dashboard on success (no checkout needed)
      window.location.href = '/dashboard'
    } catch (error) {
      console.error('Registration failed:', error)
      setApiError(t('register.errors.registrationFailed'))
    }
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">{t('register.title')}</CardTitle>
          <CardDescription>
            {t('register.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <SocialButtons mode="register" onError={setApiError} />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t('register.divider')}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email">{t('register.form.email.label')}</label>
              <Input
                id="email"
                type="email"
                placeholder="m@example.com"
                autoComplete="email"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <label htmlFor="username">{t('register.form.username.label')}</label>
              <Input
                id="username"
                placeholder="yourname"
                autoComplete="username"
                {...register('username')}
              />
              {errors.username && (
                <p className="text-sm text-destructive">{errors.username.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <label htmlFor="firstName">{t('register.form.firstName.label')}</label>
              <Input
                id="firstName"
                placeholder="John"
                {...register('firstName')}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="lastName">{t('register.form.lastName.label')}</label>
              <Input
                id="lastName"
                placeholder="Doe"
                {...register('lastName')}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password">{t('register.form.password.label')}</label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <label htmlFor="confirmPassword">{t('register.form.confirmPassword.label')}</label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword')}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <input
                id="acceptTerms"
                type="checkbox"
                {...register('acceptTerms')}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="acceptTerms" className="text-sm text-muted-foreground">
                {t('register.form.acceptTerms.label')}
              </label>
            </div>
            {errors.acceptTerms && (
              <p className="text-sm text-destructive">{errors.acceptTerms.message}</p>
            )}

            {/* Plan Selection */}
            <div className="space-y-3">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Select Your Plan
              </label>
              {isLoadingPlans ? (
                <div className="text-sm text-muted-foreground">Loading plans...</div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {plans.map((plan) => (
                    <label
                      key={plan.slug}
                      className={`p-3 border rounded-lg text-left transition-colors cursor-pointer hover:border-primary/50 ${
                        selectedPlanId === plan.slug
                          ? 'border-primary bg-primary/5'
                          : 'border-border'
                      }`}
                    >
                      <input
                        type="radio"
                        value={plan.slug}
                        {...register('planId')}
                        className="sr-only"
                      />
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{plan.name}</div>
                          <div className="text-sm text-muted-foreground">{plan.price_formatted}/mo</div>
                        </div>
                        {plan.is_featured && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                            Popular
                          </span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              {errors.planId && (
                <p className="text-sm text-destructive">{errors.planId.message}</p>
              )}
            </div>

            {apiError && (
              <p className="text-sm text-destructive">{apiError}</p>
            )}
            <Button type="submit" className="w-full" disabled={isSubmitting || isLoading || isLoadingPlans}>
              {isSubmitting || isLoading ? t('register.buttonLoading') : t('register.button')}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground">
            {t('register.links.hasAccount')}{' '}
            <Link to="/login" className="text-primary hover:underline">
              {t('register.links.signIn')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}